/**
 * Shared formatting helpers for the Report Builder result + drill-down tables.
 *
 * Kept in one place so the aggregated view and the run-list table
 * (BuilderRunsTable) render measure/dimension values identically.
 */

import type { Aggregation, DimensionDef, MeasureDef } from '@simcheck/shared-types';

export function aggLabel(a: Aggregation): string {
  switch (a) {
    case 'count': return 'Count';
    case 'count_distinct': return 'Distinct';
    case 'sum': return 'Sum';
    case 'avg': return 'Avg';
    case 'min': return 'Min';
    case 'max': return 'Max';
    case 'p50': return 'P50';
    case 'p95': return 'P95';
    case 'success_rate': return 'Success rate';
  }
}

export function formatMeasureValue(value: number | null, measure: MeasureDef, agg: Aggregation): string {
  if (value === null) return '—';
  if (measure.id === 'success_rate' || agg === 'success_rate') return (value * 100).toFixed(1) + '%';
  if (measure.unit === '%') return value.toFixed(measure.decimalPlaces ?? 1) + '%';
  if (measure.unit === 'kbps') {
    return value >= 1000 ? (value / 1000).toFixed(2) + ' Mbps' : Math.round(value) + ' kbps';
  }
  if (measure.unit === 'count') return Math.round(value).toLocaleString();
  if (measure.unit) return `${value.toFixed(measure.decimalPlaces ?? 1)} ${measure.unit}`;
  return value.toFixed(measure.decimalPlaces ?? 2);
}

export function formatDimValue(value: unknown, dim: DimensionDef): string {
  if (value === null || value === undefined) return '—';
  if (dim.dataType === 'enum') {
    const ev = dim.enumValues?.find((e) => e.value === value);
    if (ev) return ev.label;
  }
  return String(value);
}
